import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Tooltip } from 'antd';
import { PlayCircleOutlined, BookOutlined, CodeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { AuthContext } from '../../../config/AuthContext';
import { GetUsuariIdJutgeActivitatsId } from '../../../dao/GetUsuariIdJutgeActivitatsId';
import RevisioModal from '../activitatsId/content/RevisioModal';
import EnunciatModal from '../activitatsId/content/EnunciatModal';

function getToken(): string {
    return localStorage.getItem('authToken') || '';
}

function apiUrl(path: string): string {
    return (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061' + path;
}

const TheiaGamePanel: React.FC = () => {
    const [searchParams] = useSearchParams();
    const idActivitat = searchParams.get('idActivitat') || '';
    const idProblema = searchParams.get('idProblema') || '';

    const { userId } = useContext(AuthContext);

    const [problem, setProblem] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [provisioning, setProvisioning] = useState(false);
    const [provisionError, setProvisionError] = useState<string | null>(null);

    const [modalRevisio, setModalRevisio] = useState(false);
    const [modalEnunciat, setModalEnunciat] = useState(false);

    // Notify parent (ZeldaUnityGame) to close this iframe
    const closePanel = useCallback(() => {
        window.parent?.postMessage({
            type: 'THEIA_PANEL_CLOSE',
            activeExercise: idActivitat && idProblema
                ? { actividadId: idActivitat, problemaId: idProblema }
                : null
        }, '*');
    }, [idActivitat, idProblema]);

    // Fetch problem status
    const fetchData = useCallback(async () => {
        if (!idActivitat || !idProblema || !userId) return;
        setLoading(true);
        try {
            const data = await GetUsuariIdJutgeActivitatsId(userId + '', idActivitat);
            if (Array.isArray(data)) {
                const p = data.find((x: any) => String(x.id) === idProblema);
                setProblem(p || null);
            }
        } catch (e) {
            console.error('Error fetching problem data', e);
        } finally {
            setLoading(false);
        }
    }, [idActivitat, idProblema, userId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const [provisionStatus, setProvisionStatus] = useState<string>('Iniciando IDE...');

    // Provision Theia and navigate in this iframe to IdeExamFrameV2
    const handleStartIDE = useCallback(async () => {
        if (provisioning) return;
        setProvisioning(true);
        setProvisionError(null);
        setProvisionStatus('Iniciando IDE...');

        try {
            const folderName = (problem?.prac || problem?.nom || `Problema_${idProblema}`)
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');

            // Register init attempt (non-blocking)
            fetch(apiUrl('/jutge/enviament/init'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify({ id_problema: idProblema, id_activitat: idActivitat, id_usuari: userId, origen: 'UNITY' }),
            }).catch(() => { });

            // Poll until container is ready (hasta 120 intentos cada 3s = ~6 minutos para permitir arranques pesados de ECS)
            let consecutiveNetworkErrors = 0;
            const MAX_ATTEMPTS = 120;

            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                try {
                    const elapsedSecs = i * 3;
                    if (elapsedSecs > 10) {
                        setProvisionStatus(`Iniciando entorno (${elapsedSecs}s)...`);
                    }

                    const res = await fetch(
                        apiUrl(`/jutge/theia/port?id_usuari=${userId}&folder=${encodeURIComponent(folderName)}&id_problema=${idProblema}&id_practica=${idActivitat}&admin=true`),
                        { headers: { 'Authorization': `Bearer ${getToken()}` } }
                    );

                    // Si el backend responde 202 (Accepted - Provisioning en progreso) o 503/504 transitorio
                    if (res.status === 202 || res.status === 503 || res.status === 504) {
                        consecutiveNetworkErrors = 0;
                        await new Promise(r => setTimeout(r, 3000));
                        continue;
                    }

                    if (!res.ok) {
                        consecutiveNetworkErrors++;
                        if (consecutiveNetworkErrors > 6) {
                            throw new Error(`HTTP ${res.status}`);
                        }
                        await new Promise(r => setTimeout(r, 3000));
                        continue;
                    }

                    consecutiveNetworkErrors = 0;
                    const data = await res.json();

                    if (data.status === 'provisioning' || data.hostname === 'Pending...' || data.port === 0) {
                        await new Promise(r => setTimeout(r, 3000));
                        continue;
                    }

                    const projectName = data.normalizedProjectName || folderName;
                    let base = data.httpsUrl || `http://${data.hostname || window.location.hostname}:${data.port}`;
                    if (base.endsWith('/')) base = base.slice(0, -1);
                    const theiaUrl = `${base}/#/mnt/efs/${userId}/${projectName}`;

                    // Enviar mensaje al parent (ZeldaUnityGame) para que cambie el iframe a pantalla completa con el IDE
                    window.parent?.postMessage({
                        type: 'THEIA_PANEL_OPEN_IDE',
                        url: `/ide/exam-v2?url=${encodeURIComponent(theiaUrl)}&idPractica=${idActivitat}&idProblema=${idProblema}&fromGame=true`
                    }, '*');
                    return;
                } catch (netErr: any) {
                    consecutiveNetworkErrors++;
                    // Tolerar hasta 6 fallos de red transitorios antes de desistir
                    if (consecutiveNetworkErrors > 6) {
                        throw netErr;
                    }
                    await new Promise(r => setTimeout(r, 3000));
                }
            }
            throw new Error("El contenedor tardó más de lo esperado en arrancar.");
        } catch (e: any) {
            setProvisionError(`Error iniciando IDE: ${e.message}`);
        } finally {
            setProvisioning(false);
            setProvisionStatus('Iniciar IDE');
        }
    }, [provisioning, idActivitat, idProblema, userId, problem]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200, gap: 14 }}>
                <Spin size="large" />
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>Cargando el ejercicio...</Typography.Text>
            </div>
        );
    }

    if (!problem) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200, padding: 24, gap: 12 }}>
                <Alert
                    type="error"
                    message="Ejercicio no encontrado"
                    description={`No se encontró el ejercicio #${idProblema} en la actividad #${idActivitat}. userId=${userId}`}
                    showIcon
                />
                <Button size="small" onClick={closePanel}>Cerrar</Button>
            </div>
        );
    }

    const isCompleted = problem.progres >= 1;
    const title = problem.prac || problem.nom || `Ejercicio #${idProblema}`;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 200,
            padding: 24,
            background: '#f8f9fb',
        }}>
            <div style={{
                background: '#fff',
                padding: '28px 36px',
                borderRadius: 14,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 18,
                minWidth: 300,
                maxWidth: 420,
                width: '100%',
            }}>
                {/* Icon + title */}
                <div style={{ textAlign: 'center' }}>
                    {isCompleted ? (
                        <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a', display: 'block', marginBottom: 10 }} />
                    ) : (
                        <CodeOutlined style={{ fontSize: 40, color: '#1890ff', display: 'block', marginBottom: 10 }} />
                    )}
                    <Typography.Title level={4} style={{ margin: 0, fontSize: 18 }}>
                        {title}
                    </Typography.Title>
                    {isCompleted && (
                        <Typography.Text type="success" style={{ fontWeight: 600, fontSize: 13 }}>
                            ¡Ejercicio Completado! ✓
                        </Typography.Text>
                    )}
                </div>

                {/* Error */}
                {provisionError && (
                    <Alert type="error" message={provisionError} showIcon style={{ width: '100%', fontSize: 12 }} />
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                    {!isCompleted && (
                        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                            {/* Durante el aprovisionamiento el propio botón "Iniciar IDE" pasa a
                                estado loading (spinner) y el botón "Salir" desaparece, de modo que
                                el layout no cambia de tamaño ni se rompe el frame. */}
                            <Button
                                type="primary"
                                size="large"
                                icon={provisioning ? undefined : <PlayCircleOutlined />}
                                loading={provisioning}
                                onClick={handleStartIDE}
                                style={{ flex: 1, borderRadius: 8, height: 44, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                {provisioning ? provisionStatus : 'Iniciar IDE'}
                            </Button>
                            {!provisioning && (
                                <Button
                                    danger
                                    type="primary"
                                    size="large"
                                    onClick={closePanel}
                                    style={{ borderRadius: 8, height: 44, fontSize: 15 }}
                                >
                                    Salir
                                </Button>
                            )}
                        </div>
                    )}

                    {isCompleted && (
                        <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'center' }}>
                            {/* Solo icono para no cortar el texto; el tooltip + aria-label mantienen la accesibilidad */}
                            <Tooltip title="Ver código enviado">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<CodeOutlined />}
                                    onClick={() => { window.parent?.postMessage({ type: 'THEIA_PANEL_EXPAND' }, '*'); setModalRevisio(true); }}
                                    aria-label="Ver código enviado"
                                    style={{ borderRadius: 8, width: 52, height: 44 }}
                                />
                            </Tooltip>
                            <Tooltip title="Ver enunciado">
                                <Button
                                    size="large"
                                    icon={<BookOutlined />}
                                    onClick={() => { window.parent?.postMessage({ type: 'THEIA_PANEL_EXPAND' }, '*'); setModalEnunciat(true); }}
                                    aria-label="Ver enunciado"
                                    style={{ borderRadius: 8, width: 52, height: 44 }}
                                />
                            </Tooltip>
                            <Button
                                danger
                                size="large"
                                onClick={closePanel}
                                style={{ borderRadius: 8, height: 44, paddingLeft: 20, paddingRight: 20 }}
                            >
                                Salir
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {modalRevisio && (
                <RevisioModal
                    element={{ ...problem, id_usuari: userId, id_practica: idActivitat }}
                    visible={modalRevisio}
                    onClose={(_: boolean) => { setModalRevisio(false); window.parent?.postMessage({ type: 'THEIA_PANEL_COLLAPSE' }, '*'); }}
                />
            )}

            {modalEnunciat && (
                <EnunciatModal
                    element={{ ...problem, id_usuari: userId, id_practica: idActivitat }}
                    id_activitat={idActivitat}
                    visible={modalEnunciat}
                    onClose={() => { setModalEnunciat(false); window.parent?.postMessage({ type: 'THEIA_PANEL_COLLAPSE' }, '*'); }}
                />
            )}
        </div>
    );
};

export default TheiaGamePanel;
